import type { User } from '@/modules/user/model';
import { errorHandling } from '@/services/errorHandling';
import { createFormData } from '@/shared/api/create-form-data';
import { get, getBlob, post, put, patch, postForm } from '@/shared/api/fetch-based-http-methods';
import { ISubmitFormResult } from '@/shared/models/formSubmit';
import { UploadResult } from '@/shared/models/upload-result';
import { prepareUploadedFile, UploadedFile } from '@/shared/models/uploaded-file';
import { IDocUploaderForm } from '@/shared/store/doc-uploader/types';
import { formatDate } from '@/shared/utils/formatDate';
import { getApiUrl } from '@/shared/utils/getApiUrl';
import { prepareErrors } from '@/shared/utils/prepareErrors';
import { skipNullishDeep } from '@/shared/utils/skip-nullish-deep';
import { w4dPartnerRemoteAPI } from '@src-common/api/definitions/w4d-partner-endpoints';
import {
  TPartner as RawPartner,
  TPartnerRequest as UpdatePartner,
} from '@src-common/api/definitions/w4d-partner-types';
import { createClient } from '@src-common/api/src/create-client';
import { SerializableError } from '@src-common/utils/SerializableError';

import { IOnboarding, IPartner, OnboardingStep, TUpdateContactInfoForm } from '../model';
import { PaymentInfoForm } from '../model/form';
import { PartnerDocType, PartnerDocDefinition } from '../model/partner-doc-type';
import { TPartnerType } from '../model/partnerType';

// This const is used when needs to update partner fields but the user still has not input a few required fields.
// This value will be used for the required field on sending to the server and replaced with an empty string on the client.
const SPECIAL_STRING = '👹🤡💩👻💀☠️👽';

const client = createClient(
  w4dPartnerRemoteAPI({ apiHost: getApiUrl(), get, post, put, patch, postForm }),
);

const prepareLegalName = (
  raw: Partial<RawPartner>,
): Pick<IPartner, 'legalName' | 'vatRegNo' | 'taxProfile'> => ({
  legalName: raw.legal_info?.name === SPECIAL_STRING ? '' : (raw.legal_info?.name ?? ''),
  vatRegNo: raw.legal_info?.vat_reg_no ?? '',
  taxProfile: raw.legal_info?.tax_profile,
});

const prepareOnboarding = (rawOnboarding: RawPartner['onboarding']): IOnboarding => {
  if (!rawOnboarding) {
    return {
      isCompleted: true,
      lastStepName: OnboardingStep['payment'],
      isLastStepCompleted: true,
    };
  }

  return {
    isCompleted: rawOnboarding?.completed || false,
    lastStepName: rawOnboarding?.last_step_name || 'welcome',
    isLastStepCompleted: rawOnboarding?.last_step_completed || false,
  };
};

type AddressRaw = NonNullable<NonNullable<RawPartner['legal_info']>['registered_address']>;

type RegisteredAddress =
  (Omit<AddressRaw, 'zip_code'> & { zipCode?: string | null | undefined }) | undefined;

const prepareRegisteredAddress = (
  registeredAddress: AddressRaw | null | undefined,
): RegisteredAddress => {
  if (!registeredAddress) {
    return;
  }

  const { zip_code: zipCode, ...rest } = registeredAddress;

  return { ...rest, zipCode };
};

const preparePartnerOperator = (
  partnerOperator?: boolean,
): 'wheely_tech' | 'wheely_london' | undefined => {
  if (typeof partnerOperator === 'boolean') {
    return partnerOperator ? 'wheely_tech' : 'wheely_london';
  }

  return;
};

const preparePartner = (
  raw: RawPartner,
): Omit<IPartner, 'firstName' | 'lastName' | 'middleName' | 'userId' | 'source'> => ({
  id: raw.id,
  referralCode: '',
  email: raw.email || '',
  countryCode: raw.city?.country_code as any,
  phone: raw.phone,
  cityId: raw.city_id,
  cityName: raw.city_name,
  companyRegNumber: raw.legal_info?.company_reg_number || '',
  personalInn: raw.personal_inn || '',
  inn: raw.legal_info?.inn || '',
  ssn: raw.legal_info?.ssn ?? '',
  ein: raw.legal_info?.ein ?? '',
  dbaName: raw.legal_info?.dba_name ?? '',
  licenseNumber: raw.legal_info?.license_number ?? '',
  legalAddress1: raw.legal_info?.registered_address?.line1 || '',
  ...prepareLegalName(raw),
  partnerType: raw.legal_info?.type || undefined,
  legalKpp: raw.legal_info?.kpp || '',
  legalNds: raw.legal_info?.nds ?? null,
  legalOgrn: raw.legal_info?.ogrn || '',
  legalRegisteredAddress: prepareRegisteredAddress(raw.legal_info?.registered_address),
  legalPostalAddress: raw.legal_info?.postal_address || undefined,
  paymentAccount: raw.payment_info?.account_number || '',
  paymentRoutingNumber: raw.payment_info?.routing_number || '',
  paymentSortCode: raw.payment_info?.sort_code || '',
  paymentBik: raw.payment_info?.bik || '',
  paymentRasAccount: raw.payment_info?.ras_account || '',
  paymentKorAccount: raw.payment_info?.kor_account || '',
  paymentSwift: raw.payment_info?.swift || '',
  paymentIban: raw.payment_info?.iban || '',
  paymentBankName: raw.payment_info?.bank_name || '',
  accessStatus: raw.access_status,
  onboarding: prepareOnboarding(raw.onboarding),
  docUploads: Object.fromEntries<UploadedFile<PartnerDocType>>(
    raw.uploads?.map(rawUpload => [
      rawUpload.type,
      prepareUploadedFile<PartnerDocType>(rawUpload),
    ]) ?? [],
  ),
  isVehiclesHidden: raw.vehicles_hidden,
  taxRegNo: raw.legal_info?.tax_reg_no ?? '',
  tradeLicenseNumber: raw.legal_info?.trade_license_number ?? '',
  registrationAuditStep: raw.registration_audit_step || null,
  hasPhoLicense: raw.legal_info?.has_pho_license ?? null,
  partnerOperator: preparePartnerOperator(raw.partner_operator),
});

const prepareUpdatePartner = (partner: IPartner): UpdatePartner => ({
  access_status: partner.accessStatus,
  city_id: partner.cityId,
  email: partner.email,
  phone: partner.phone,
  vehicles_hidden: partner.isVehiclesHidden,
  legal_info: {
    registered_address: partner.legalRegisteredAddress || null,
    postal_address: partner.legalPostalAddress || null,
    inn: partner.inn || null,
    kpp: partner.legalKpp || null,
    company_reg_number: partner.companyRegNumber || null,
    vat_reg_no: partner.vatRegNo || null,
    nds: partner.legalNds,
    ogrn: partner.legalOgrn || null,
    name: partner.legalName || null,
    type: partner.partnerType || null,
    ssn: partner.ssn || null,
    ein: partner.ein || null,
    dba_name: partner.dbaName || null,
    license_number: partner.licenseNumber || null,
    trade_license_number: partner.tradeLicenseNumber || null,
    tax_profile: partner.taxProfile || null,
    has_pho_license: partner.hasPhoLicense,
  },
  payment_info: {
    account_number: partner.paymentAccount || null,
    bank_name: partner.paymentBankName || null,
    routing_number: partner.paymentRoutingNumber || null,
    sort_code: partner.paymentSortCode || null,
    bik: partner.paymentBik || null,
    swift: partner.paymentSwift || null,
    iban: partner.paymentIban || null,
    kor_account: partner.paymentKorAccount || null,
    ras_account: partner.paymentRasAccount || null,
  },
  registration_audit_step: partner.registrationAuditStep,
  promo: null,
});

class PartnerBlockedError extends SerializableError('PartnerBlockedError') {}

const partnerAPI = {
  createPartner: async (user: User) => {
    const response = await client.createPartner({
      payload: {
        partner: { city_id: user.cityId },
        utm_tags: (window as any).UTM_TAGS ?? {},
      },
    });

    if (response.errors) {
      return { errors: response.errors };
    }

    return {
      partner: {
        firstName: user.firstName,
        lastName: user.lastName,
        middleName: user.middleName,
        userId: user.userId,
        source: user.source,
        ...preparePartner(response.partner),
      },
    };
  },

  readPartner: async (user: User): Promise<IPartner> => {
    const response = await client.readPartner();

    if (response.errors) {
      throw new PartnerBlockedError();
    }

    return {
      firstName: user.firstName,
      lastName: user.lastName,
      middleName: user.middleName,
      userId: user.userId,
      source: user.source,
      ...preparePartner(response.partner),
    };
  },

  updatePaymentInfo: async (partner: IPartner): Promise<ISubmitFormResult<PaymentInfoForm>> => {
    const response = await client.updatePartner({
      payload: prepareUpdatePartner(partner),
    });

    return {
      baseErrors: prepareErrors(response.errors),
    };
  },

  updateIsVehiclesHidden: async (
    partner: IPartner,
  ): Promise<ISubmitFormResult<{ isVehiclesHidden: boolean }>> => {
    const response = await client.updatePartner({
      payload: prepareUpdatePartner(partner),
    });

    return {
      baseErrors: prepareErrors(response.errors),
    };
  },

  updateContactInfo: async (
    partner: IPartner,
  ): Promise<ISubmitFormResult<TUpdateContactInfoForm>> => {
    const response = await client.updatePartner({
      payload: prepareUpdatePartner(partner),
    });

    const result: ISubmitFormResult<TUpdateContactInfoForm> = {};

    if (response.errors?.user_exists) {
      result.validationErrors = {
        phoneNumber: response.errors.user_exists,
      };
      delete response.errors.user_exists;
    }

    result.baseErrors = prepareErrors(response.errors);

    return result;
  },

  fetchDocDefinitions: async (partnerType: TPartnerType): Promise<Array<PartnerDocDefinition>> => {
    const response = await client.readPartnerDocumentDefinitions({ pathParams: { partnerType } });

    if ('errors' in response) {
      errorHandling.captureMessage(
        'Failed to fetch doc definitions: ' + JSON.stringify(response.errors),
      );

      return [];
    }

    return response.map(raw => ({
      docType: raw.type,
      title: raw.caption,
      comment: raw.comment || undefined,
      desc: raw.description || undefined,
      exampleUrl: raw.example_url || undefined,
      isOptional: raw.optional,
      isExpires: raw.expires || false,
    }));
  },

  uploadFile: async (
    form: IDocUploaderForm<PartnerDocType>,
    holderId: string,
  ): Promise<UploadResult<PartnerDocType>> => {
    const response = await client.uploadPartnerDocument({
      pathParams: { partnerId: holderId },
      payload: createFormData(
        skipNullishDeep(
          {
            type: form.docType,
            expires_at: form.expiresAt && formatDate(form.expiresAt), // YYYY-MM-DD
            upload_file: form.file?.file,
          },
          false,
        ),
      ),
    });

    if (!('upload' in response)) {
      if ('errors' in response) {
        if (response.errors.file) {
          return {
            error: response.errors.file[0],
          };
        }

        if (response.errors.error) {
          return {
            error: response.errors.error[0],
          };
        }
      }
    }

    if ('upload' in response) {
      return {
        id: response.upload.id,
        type: response.upload.type,
      };
    }

    return {
      error: '',
    };
  },

  /**
   * The agent agreement confirmation PDF, signed with the session's Bearer:
   * the route takes no short_token, and the ingress drops unsigned calls.
   */
  getAgencyAgreementConfirmation: (): Promise<Blob> =>
    getBlob(`${getApiUrl()}/v6/partners/self/agent_agreement_confirmation?inline=1`),

  getPaymentInfoByBIK: async (bik: string) => client.readInfoByBIK({ pathParams: { bik } }),
};

export { partnerAPI, PartnerBlockedError };
